// Module-level language state. Kept outside React context so non-React modules
// (format helpers, role labels) can read the active locale without prop drilling.

export type Lang = "en" | "nl" | "fr" | "de";

export const LANGS: Lang[] = ["en", "nl", "fr", "de"];
export const DEFAULT_LANG: Lang = "en";
const STORAGE_KEY = "ecerium.lang";

const LOCALES: Record<Lang, string> = {
  en: "en-GB",
  nl: "nl-BE",
  fr: "fr-BE",
  de: "de-DE",
};

function isLang(value: unknown): value is Lang {
  return typeof value === "string" && (LANGS as string[]).includes(value);
}

function detectInitial(): Lang {
  // Marketplace controls the language via the ?lang= query param on the iframe
  // URL. That always wins so the app stays in sync with the surrounding UI.
  try {
    const urlLang = new URLSearchParams(window.location.search).get("lang");
    if (isLang(urlLang)) return urlLang;
  } catch {
    // Non-browser context — fall through.
  }
  // Fallbacks for standalone mode (custom domain, no marketplace).
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLang(stored)) return stored;
  } catch {
    // localStorage may be blocked — fall through to navigator detection.
  }
  const nav = typeof navigator !== "undefined" ? navigator.language?.slice(0, 2) : null;
  if (isLang(nav)) return nav;
  return DEFAULT_LANG;
}

let currentLang: Lang = detectInitial();
const listeners = new Set<(lang: Lang) => void>();

export function getLang(): Lang {
  return currentLang;
}

// Accepts any IETF-style code, not just the four in LANGS, so a public
// intake form pinned to "it" or "es" via /form/<slug>/<lang> can flip
// the UI labels to Italian / Spanish where translations exist and
// fall back to English (in tr()) where they don't.
export function setLang(lang: string): void {
  if (lang === currentLang) return;
  currentLang = lang as Lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // ignore — preference simply won't persist
  }
  document.documentElement.lang = lang;
  for (const listener of listeners) listener(lang as Lang);
}

export function subscribe(listener: (lang: Lang) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function localeFor(lang: Lang = currentLang): string {
  return LOCALES[lang];
}
